import { useId, useState, type ReactNode } from 'react'
import { MERCHANTS_PATH } from '../merchants/merchantPaths'
import { InlineLink } from '../../platform/primitives/InlineLink'
import { Money } from '../../platform/primitives/Money'
import { Panel } from '../../platform/primitives/Panel'
import { useBreakpoint } from '../../platform/responsive/useBreakpoint'
import { SegmentedControl, type Segment } from '../../platform/primitives/SegmentedControl'
import { type LinkTarget } from './cashflowScope'
import { categoryName, UNCATEGORISED, type CategoryMap } from './cashflowCategories'
import { deltaPct, shortDelta } from './cashflowRules'
import type { Breakdown, MerchantSpend } from './cashflowWire'
import { RankedList, type RankRow } from './RankedList'

export type WentView = 'categories' | 'groups' | 'merchants'

const VIEWS: readonly Segment<WentView>[] = [
  { value: 'categories', label: 'Categories' },
  { value: 'groups', label: 'Groups' },
  { value: 'merchants', label: 'Merchants' },
]

/** Keys a server id cannot take: category and merchant ids are never prefixed with "~". */
const UNCATEGORISED_KEY = '~uncategorised'
const OTHER_KEY = '~other-merchants'
const NO_MERCHANT_KEY = '~no-merchant'

/** The "No merchant" row's name, and its drill-down page's title. */
export const NO_MERCHANT = 'No merchant'

const plural = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`

/** "Up 20% vs September"; null when there is nothing to compare. */
const deltaSub = (amountCents: number, priorCents: number | null, priorPhrase: string | null): string | null =>
  priorPhrase === null ? null : shortDelta(deltaPct(amountCents, priorCents), priorPhrase)

/** "12 payments" for a recurring merchant, else "118 visits". */
const visits = (m: MerchantSpend): string =>
  m.isRecurring ? plural(m.transactionCount, 'payment', 'payments') : plural(m.transactionCount, 'visit', 'visits')

/** "184 transactions with no category yet": the Uncategorised row's sub line, in either view (R28). */
const uncategorisedLine = (count: number): string => `${plural(count, 'transaction', 'transactions')} with no category yet`

/** The rows behind the spending figure (PR 509) when the server says; the both-sides count from an older one. */
const spendingCount = (u: Breakdown['uncategorised']): number => u.expenseCount ?? u.count

/**
 * The server omits Uncategorised from `categories.items` and gives it no share,
 * so it is placed by its amount among the server-ordered rows — after any row
 * of equal amount — and draws no bar and no %.
 */
function withUncategorised(rows: RankRow[], uncategorised: Breakdown['uncategorised'], to: string | undefined): RankRow[] {
  if (uncategorised.amountCents <= 0) return rows
  const row: RankRow = {
    key: UNCATEGORISED_KEY,
    name: UNCATEGORISED,
    sub: uncategorisedLine(spendingCount(uncategorised)),
    amountCents: uncategorised.amountCents,
    sharePct: null,
    to,
  }
  const at = rows.findIndex((r) => r.amountCents < uncategorised.amountCents)
  return at === -1 ? [...rows, row] : [...rows.slice(0, at), row, ...rows.slice(at)]
}

const MORE_CAME_IN = 'More came in than went out'

/** A category or group whose refunds outweigh its spend: its own signed amount, flagged in place. */
const flagged = (key: string, name: string, amountCents: number, to: string | undefined): RankRow => ({
  key,
  name,
  sub: MORE_CAME_IN,
  amountCents,
  sharePct: null,
  flag: 'miscategorised',
  to,
})

/**
 * "Where it went" for the selected period: one ranked list with a Categories /
 * Groups / Merchants toggle, replacing the three separate panels. Every figure
 * is the server's — the subtitle is the period's `totals.expenseCents`, passed
 * in and never summed from the rows; shares are the server's `share_pct`, and
 * merchants carry none, so they draw no bar (a share is an aggregate).
 *
 * The toggle only swaps the list inside one mounted `<section>`, so the panel
 * neither jumps nor loses its place.
 */
export function WhereItWent({
  breakdown,
  names,
  currency,
  expenseCents,
  priorPhrase,
  onMerchantsExpanded,
  onViewChange,
  rowLink,
}: {
  breakdown: Breakdown
  names: CategoryMap
  currency: string
  /** The period's `totals.expenseCents`. */
  expenseCents: number
  /** 'vs September' (`priorPhrase`), null when the period has no prior. */
  priorPhrase: string | null
  /** The container emits `cashflow_merchant_expanded`; this stays presentational. */
  onMerchantsExpanded: () => void
  /** The container emits `cashflow_breakdown_view_changed`. */
  onViewChange?: (v: WentView) => void
  /** The detail path for a row; undefined draws no link. */
  rowLink: (t: LinkTarget) => string | undefined
}) {
  const headingId = useId()
  const radioName = useId()
  const [view, setView] = useState<WentView>('categories')
  // Phone: the toggle takes the full row, three equal segments on one line.
  const phone = useBreakpoint() === 'phone'

  let body: ReactNode
  if (view === 'categories') {
    // A zero row is neither spend nor flagged, so it is drawn in neither position.
    const ranked = breakdown.categories
      .filter((c) => c.amountCents > 0)
      .map<RankRow>((c) => ({
        key: c.categoryId,
        name: categoryName(names, c.categoryId),
        sub: deltaSub(c.amountCents, c.priorCents, priorPhrase),
        amountCents: c.amountCents,
        sharePct: c.sharePct,
        to: rowLink({ kind: 'category', id: c.categoryId }),
      }))
    const back = breakdown.categories
      .filter((c) => c.amountCents < 0)
      .map((c) =>
        flagged(c.categoryId, categoryName(names, c.categoryId), c.amountCents, rowLink({ kind: 'category', id: c.categoryId })),
      )
    body = (
      <RankedList
        key="categories"
        label="Spending by category"
        rows={[...withUncategorised(ranked, breakdown.uncategorised, rowLink({ kind: 'uncategorised' })), ...back]}
        currency={currency}
        showAllNoun="categories"
      />
    )
  } else if (view === 'groups') {
    const groupName = (id: string | null) => (id === null ? UNCATEGORISED : categoryName(names, id))
    const groupKey = (id: string | null) => id ?? UNCATEGORISED_KEY
    // As for categories: a zero group is drawn in neither position.
    const ranked = breakdown.groups
      .filter((g) => g.amountCents > 0)
      .map<RankRow>((g) => ({
        key: groupKey(g.groupId),
        name: groupName(g.groupId),
        // The null group is the uncategorised spend: its count says what to do about it, a delta does not (R28).
        sub:
          g.groupId === null && spendingCount(breakdown.uncategorised) > 0
            ? uncategorisedLine(spendingCount(breakdown.uncategorised))
            : deltaSub(g.amountCents, g.priorCents, priorPhrase),
        amountCents: g.amountCents,
        sharePct: g.sharePct,
        to: rowLink(g.groupId === null ? { kind: 'uncategorised' } : { kind: 'group', id: g.groupId }),
      }))
    const back = breakdown.groups
      .filter((g) => g.amountCents < 0)
      .map((g) =>
        flagged(
          groupKey(g.groupId),
          groupName(g.groupId),
          g.amountCents,
          rowLink(g.groupId === null ? { kind: 'uncategorised' } : { kind: 'group', id: g.groupId }),
        ),
      )
    body = (
      <RankedList key="groups" label="Spending by group" rows={[...ranked, ...back]} currency={currency} showAllNoun="groups" />
    )
  } else {
    const { top } = breakdown.merchants
    const rows: RankRow[] = top.map((m) => ({
      key: m.merchantId,
      name: m.name,
      sub: visits(m),
      amountCents: m.amountCents,
      sharePct: null,
      to: rowLink({ kind: 'merchant', id: m.merchantId }),
    }))
    const { split } = breakdown.merchants
    // Backend 512: the remainder in its two halves, each drawn only when it holds a transaction.
    // The merchants past the top N are a count, not a place; the rows with no merchant are a scope.
    if (split.other.transactionCount > 0) {
      rows.push({
        key: OTHER_KEY,
        name: plural(split.other.count, 'other merchant', 'other merchants'),
        sub: null,
        amountCents: split.other.amountCents,
        sharePct: null,
      })
    }
    if (split.unassigned.transactionCount > 0) {
      rows.push({
        key: NO_MERCHANT_KEY,
        name: NO_MERCHANT,
        sub: plural(split.unassigned.transactionCount, 'transaction', 'transactions'),
        amountCents: split.unassigned.amountCents,
        sharePct: null,
        to: rowLink({ kind: 'no_merchant' }),
      })
    }
    body = (
      <>
        {/* The server's top N, plus its remainder's two halves: never a Show all. */}
        {rows.length === 0 ? (
          <p className="cf-panel__sub">No merchant spending this period.</p>
        ) : (
          <RankedList
            key="merchants"
            label="Spending by merchant"
            rows={rows}
            currency={currency}
            top={rows.length}
            showAllNoun="merchants"
          />
        )}
        <InlineLink to={MERCHANTS_PATH} className="cf-went__all" onClick={onMerchantsExpanded}>
          All merchants <span aria-hidden="true">→</span>
        </InlineLink>
      </>
    )
  }

  return (
    <section aria-labelledby={headingId} className={phone ? 'cf-went cf-went--phone' : 'cf-went'}>
      <Panel className="cf-panel">
        <div className="cf-panel__bar">
          <div className="cf-panel__head">
            <h2 id={headingId} className="cf-panel__title">
              Where it went
            </h2>
            <p className="cf-panel__sub">
              <Money cents={expenseCents} currency={currency} display="whole" /> spent
            </p>
          </div>
          <SegmentedControl label="Break down by" name={radioName} options={VIEWS} value={view} onChange={(v) => {
              setView(v)
              onViewChange?.(v)
            }}
          />
        </div>
        {body}
      </Panel>
    </section>
  )
}
